import React, { useState, useEffect } from 'react';
import { GiftCard } from '../../types/graphql-global-types';
import RefreshIcon from '../../assets/refresh.svg';
import CustomDate from '../custom-date/custom-date';
import util from '../../util';
import { showToast, ToastVariants } from '../index';

export interface CardProps {
    card: GiftCard;
    lineitems?: GiftCard[];
    setBalance?: (card: GiftCard) => Promise<void>;
    handlePrint?: () => void;
    updateCardSchedule?: (
        clientcardsrno: string,
        carddetid: number,
        shipdate: number,
    ) => Promise<void>;
}

const CardWrapper = (Card: Function) => {
    return function CardWrapped({ card, ...props }: CardProps) {
        const [loading, setLoading] = useState(false);

        const retry = () => {
            setLoading(true);
            setTimeout(() => {
                props.setBalance && props.setBalance(card).then(() => setLoading(false));
            }, 100);
            // 100 [ms] is just a magic number used to fake delay
            // so that user can see the loading message for some time
        };

        useEffect(() => {
            if (typeof card.balance !== 'number') {
                retry();
            }
        }, []);

        const balance = () => {
            let status = 'Failed';
            if (loading) {
                status = 'Loading...';
            } else if (typeof card.balance === 'number') {
                status = '$' + card.balance?.toFixed(2);
            }
            return (
                <span className="d-flex align-items-center">
                    {status}
                    {!loading && (
                        <img
                            alt="refresh icon"
                            src={RefreshIcon}
                            className="refresh-icon"
                            onClick={retry}
                        />
                    )}
                </span>
            );
        };

        const cardImage = () => (
            <img
                alt="card image"
                src={util.getCardImageUrl(card)}
                className="card-image"
            />
        );

        const handleDateSubmit = (date: string) => {
            if (!card.clientcardsrno || !card.carddetid) {
                showToast({
                    title: 'Failed!',
                    content: 'Card identifiers are missing.',
                    variant: ToastVariants.DANGER,
                });
                return;
            }
            let shipdate = util.getUTCDate(date);
            if (props.updateCardSchedule) {
                props.updateCardSchedule(
                    card.clientcardsrno,
                    card.carddetid,
                    shipdate,
                );
            }
        };

        return (
            <Card
                card={card}
                lineitems={props.lineitems}
                balance={balance}
                cardImage={cardImage}
                deliveryDate={() => (
                    <CustomDate shipdate={card.shipdate} handleSubmit={handleDateSubmit} />
                )}
                handlePrint={props.handlePrint}
            />
        );
    };
};

export default CardWrapper;
